import { SmartusComponentsModule } from '@affinis/smartus-components';
import { ButtonProps } from '@affinis/smartus-components/lib/button/button.types';
import { Chip } from '@affinis/smartus-components/lib/chips/chips.component.types';
import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import {
  SortDirection,
  SortService,
} from '@core/services/sort-service/sort.service';
import {
  MtxGridCellTemplate,
  MtxGridColumn,
  MtxGridModule,
} from '@ng-matero/extensions/grid';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { ModalFilterComponent } from '../modal-filter/modal-filter.component';
import {
  DEFAULT_TABLE_CONFIG,
  IDialogData,
  ITableConfig,
} from './data-table.types';

@Component({
  standalone: true,
  selector: 'app-data-table',
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss'],
  imports: [
    CommonModule,
    MtxGridModule,
    SmartusComponentsModule,
    TranslateModule,
  ],
})
export class DataTableComponent implements OnChanges {
  // Table props
  @Input() columns: Array<MtxGridColumn> = [];
  @Input() config: ITableConfig = DEFAULT_TABLE_CONFIG;
  @Input() data: unknown[] = [];
  @Input() expansionTemplate: TemplateRef<void>;
  @Input() extraButtons: ButtonProps[] = [];
  @Input() headerTemplate: TemplateRef<unknown> | MtxGridCellTemplate;
  @Input() isLoading = false;
  @Input() componentIdentifier = 'default';
  @Input() totalCount: number | undefined;
  @Input() translationPath = '';
  @Input() selection: Observable<unknown[]> = of([]);

  @Output() changedPage = new EventEmitter<PageEvent>();
  @Output() filterChanged = new EventEmitter<any>();
  @Output() filteredDataChanged = new EventEmitter<unknown[]>();
  @Output() refreshData = new EventEmitter();
  @Output() rowClick = new EventEmitter();
  @Output() rowSelectedChange = new EventEmitter<any>();
  @Output() sortChanged = new EventEmitter<Sort>();

  chips: Chip[] = [];
  controlButtons: ButtonProps[] = [];
  filteredData: unknown[] = [];
  modalComponent = ModalFilterComponent;

  dialogData: IDialogData = {
    columns: this.columns,
    filterFields: this.config.filterFields ?? [],
    filterValues: this.config.filterOptions,
    actions: {
      onSubmit: (formModel: any) => {
        this.mapFormModelToChips(formModel);
        this.updateFilter(formModel);
        this.filterData();
      },
      onReset: () => {
        this.mapFormModelToChips({});
        this.updateFilter({});
        this.filterData();
      },
    },
  };

  constructor(private translateService: TranslateService) {
    this.controlButtons = [
      {
        label: this.translateService.instant('crud.refresh'),
        icon: 'rotate',
        onClick: () => {
          this.refresh();
        },
      },
    ];
  }

  ngOnChanges(changes: SimpleChanges): void {
    for (const propName in changes) {
      if (Object.prototype.hasOwnProperty.call(changes, propName)) {
        if (propName === 'data') {
          this.filterData();
        } else if (propName === 'config') {
          this.dialogData.filterFields = this.config.filterFields ?? [];
          this.dialogData.filterValues = this.config.filterOptions;
          this.mapFormModelToChips(this.config.filterOptions);
        } else if (propName === 'extraButtons') {
          this.controlButtons = [...this.extraButtons, ...this.controlButtons];
        }
      }
    }
  }

  ngAfterViewInit() {
    setTimeout(() => {
      const headers = document.querySelectorAll('.mtx-grid .mat-sort-header');
      headers.forEach(header => {
        const el = header as HTMLElement;

        if (el.classList.contains('mat-sort-header-disabled')) {
          el.setAttribute('tabindex', '0');
        }
      });
    });
  }

  refresh() {
    this.refreshData.emit();
  }

  filterData() {
    if (!this.config.filterOnFront) {
      this.filteredData = this.data;
      return;
    }

    this.filteredData = structuredClone(this.data);
    const filterOptions = this.config.filterOptions;

    for (const key of Object.keys(filterOptions)) {
      const filterValue = filterOptions[key];

      if (
        filterValue == null ||
        filterValue === '' ||
        filterValue.length === 0
      ) {
        continue;
      }

      this.filteredData = this.filteredData?.filter((x: any) => {
        const data = x[key];

        if (Array.isArray(filterValue)) {
          return this.filterArray(data, filterValue);
        }

        if (typeof data === 'string') {
          return this.filterString(data, filterValue);
        }

        return data == filterValue;
      });
    }
    setTimeout(() => {
      this.filteredData = [...this.filteredData];
    }, 0);
    this.filteredDataChanged.emit(this.filteredData);
  }

  handleOnRemoveChip = (eventObj: any) => {
    delete this.config.filterOptions[eventObj.id];
    this.filterChanged.emit(this.config.filterOptions);
    this.chips = this.chips.filter(chipItem => chipItem.id !== eventObj.id);
    this.filterData();
  };

  updateFilter(filterOptions: any) {
    this.config.pageIndex = 0;
    this.config.filterOptions = filterOptions;
    this.dialogData.filterValues = this.config.filterOptions;
    this.filterChanged.emit(this.config.filterOptions);
  }

  sortChange(sort: Sort) {
    if (!this.config.sortOnFront) {
      this.sortChanged.emit(sort);
      return;
    }

    if (sort.direction === '') {
      this.filterData();
      return;
    }

    this.filteredData = [
      ...SortService.sort(
        this.filteredData,
        sort.direction as SortDirection,
        sort.active
      ),
    ];
  }

  changePage(e: PageEvent) {
    this.config.pageIndex = e.pageIndex;
    this.config.pageSize = e.pageSize;
    this.changedPage.emit(e);
  }

  private filterArray(data: any, filterValue: any[]): boolean {
    if (!Array.isArray(data)) {
      return filterValue.includes(data);
    }

    return data.some(y => filterValue.includes(y));
  }

  private filterString(data: string, filterValue: string): boolean {
    return data.toLowerCase().includes(filterValue.toLowerCase());
  }

  /**
   * Retrieves the translated value for a given translation key.
   * If no translation exists for the specified key, returns the provided fallback value.
   *
   * @param {string} translationKey - The key for the desired translation.
   * @param {any} fallbackValue - The value to return if no translation exists for the given key.
   * @returns {string} - The translated value or the fallback value.
   */
  private getTranslatedValueOrFallback(
    translationKey: string,
    fallbackValue: any
  ): string {
    const translatedValue = this.translateService.instant(translationKey);
    return translatedValue !== translationKey ? translatedValue : fallbackValue;
  }

  private mapFormModelToChips(formModel: object) {
    const tmpChips: Chip[] = [];
    Object.keys(formModel).forEach(formModelItemKey => {
      const field = this.config.filterFields
        .flatMap(x => x.fieldGroup)
        .flatMap(x => x.fieldGroup ?? x)
        ?.find(fieldItem => {
          return fieldItem.key === formModelItemKey;
        });

      if (!field) {
        return;
      }

      if (field.props?.getChipLabel) {
        const labelToDisplay = field.props.getChipLabel(
          formModel[formModelItemKey]
        );
        tmpChips.push({
          id: formModelItemKey,
          label: labelToDisplay,
        });
      } else if (field.type === 'datepicker') {
        const date = new Date(formModel[formModelItemKey]).toLocaleDateString(
          'de-DE'
        );
        tmpChips.push({
          id: formModelItemKey,
          label: `${field.props.label}: ${date}`,
        });
      } else if (formModel[formModelItemKey]) {
        const valueToDisplay = this.getTranslatedValueOrFallback(
          `${this.translationPath}.${formModel[formModelItemKey]}`,
          formModel[formModelItemKey]
        );

        tmpChips.push({
          id: formModelItemKey,
          label: `${field.props?.label}: ${valueToDisplay}`,
        });
      }
    });
    this.chips = tmpChips;
  }
}
